import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpInterceptorFn,
  type HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, map, type Observable, of, tap, throwError } from 'rxjs';
import type { SelectionResponse } from '../../models/selections';
import { OfflineSessionService } from '../../services/offline/offline-session';
import { OfflineStore } from '../../services/offline/offline-store';
import { OFFLINE_SYNC_REQUEST } from '../../services/offline/offline-sync';
import { matchOfflineWrite } from '../../services/offline/offline-writes';

/** `error` of the HttpErrorResponse returned for calls that need connection. */
export const OFFLINE_UNAVAILABLE = 'OFFLINE_UNAVAILABLE';

const API_PATH = '/nubereader';
/** Served by the service worker cache, so they can go through. */
const PASS_THROUGH_PATHS = ['/nubereader/resources/'];
const SELECTIONS_PATH = '/nubereader/selections';

function pathOf(url: string): string {
  return new URL(url, location.origin).pathname;
}

function isSelectionList(value: unknown): value is SelectionResponse[] {
  return Array.isArray(value);
}

function offlineResponse(
  request: HttpRequest<unknown>,
  path: string,
  services: { session: OfflineSessionService; store: OfflineStore },
): Observable<HttpEvent<unknown>> {
  const { session, store } = services;
  if (request.method === 'GET' && path === SELECTIONS_PATH) {
    const body = session.snapshot()?.selections ?? [];
    return of(new HttpResponse({ status: HttpStatusCode.Ok, url: request.url, body }));
  }
  const route = matchOfflineWrite(request.method, path);
  if (!route) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 0,
          statusText: 'Offline',
          url: request.url,
          error: OFFLINE_UNAVAILABLE,
        }),
    );
  }
  return from(store.recordOffline(request, route)).pipe(
    map((body) => new HttpResponse({ status: HttpStatusCode.Ok, url: request.url, body })),
  );
}

/**
 * Offline reading. In an offline session the API is not reachable: reads are answered from the
 * downloaded copy, supported writes are queued for the next sync and answered locally, and every
 * other call fails at once with {@link OFFLINE_UNAVAILABLE}. Online, when the book is also
 * downloaded, successful writes are applied to that copy so it stays current.
 */
export const offlineInterceptor: HttpInterceptorFn = (request, next) => {
  const path = pathOf(request.url);
  if (
    request.context.get(OFFLINE_SYNC_REQUEST) ||
    !path.startsWith(API_PATH) ||
    PASS_THROUGH_PATHS.some((prefix) => path.startsWith(prefix))
  ) {
    return next(request);
  }

  const session = inject(OfflineSessionService);
  const store = inject(OfflineStore);
  if (session.isOffline()) return offlineResponse(request, path, { session, store });

  const loanKey = session.downloadedLoan();
  if (!loanKey) return next(request);
  const write = matchOfflineWrite(request.method, path);
  // Reading sessions are only queued offline: the downloaded copy does not keep them.
  const route = write?.kind === 'reading' ? undefined : write;
  const isSelectionsRead = request.method === 'GET' && path === SELECTIONS_PATH;
  if (!route && !isSelectionsRead) return next(request);
  return next(request).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse) || !event.ok) return;
      if (route) store.recordOnline(loanKey, { request, route, response: event.body });
      else if (isSelectionList(event.body)) store.recordOnlineSelections(loanKey, event.body);
    }),
  );
};
